import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth, useTrip } from '@trip-planner-monorepo/shared';
import './FileMenu.css';
import { FiFolder } from 'react-icons/fi';

/**
 * FileMenu Component
 *
 * Provides a comprehensive File menu for the Trip Planner application with
 * context-aware export/import functionality. Replaces the complex ExportImportModal
 * with a simplified, menu-driven approach.
 *
 * Menu Structure:
 * - New Trip (Always available)
 * - Open Trip (Always available)
 * - ─────────────
 * - Save PDF file (Context-aware page export)
 * - Export Itinerary (Days page XLS/CSV export)
 * - Export Trip (Complete ZIP export)
 * - Import Trip (Complete ZIP/JSON import)
 * - ─────────────
 * - Backup Trip (Registry-based backup)
 * - Restore Trip (Registry-based restore)
 * - ─────────────
 * - Print (To be specified - currently disabled)
 * - ─────────────
 * - Settings (Links to existing settings)
 *
 * @param {Object} props - Component props
 * @param {Function} props.onNewTrip - Handler for new trip
 * @param {Function} props.onOpenTrip - Handler for open trip
 * @param {Function} props.onSavePDF - Handler for PDF export
 * @param {Function} props.onExportItinerary - Handler for itinerary export
 * @param {Function} props.onExportTrip - Handler for trip export
 * @param {Function} props.onImportTrip - Handler for trip import
 * @param {Function} props.onPrint - Handler for print
 * @param {Function} props.onSettings - Handler for settings
 */
const FileMenu = ({
  onNewTrip,
  onOpenTrip,
  onSavePDF,
  onExportItinerary,
  onExportTrip,
  onImportTrip,
  onPrint,
  onSettings
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { currentUser } = useAuth();
  const { selectedTripId, currentTrip } = useTrip();
  const location = useLocation();

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Context detection for menu availability
  const isOnContentPage = location.pathname.includes('/trips/');
  const _isOnDaysPage = location.pathname.includes('/trips/') &&
    !location.pathname.includes('/places') &&
    !location.pathname.includes('/travel') &&
    !location.pathname.includes('/accommodation') &&
    !location.pathname.includes('/documents') &&
    !location.pathname.includes('/extras');
  const hasActiveTripWithDays = currentTrip && selectedTripId;

  // Shared-trip role checks: only editors (and owners) may export a trip
  const isViewerOfSharedTrip = currentTrip?.isShared && currentTrip?.sharedRole !== 'editor';

  // Menu item click handlers that delegate to props
  const handleNewTrip = () => {
    handleClose();
    if (onNewTrip) onNewTrip();
  };

  const handleOpenTrip = () => {
    handleClose();
    if (onOpenTrip) onOpenTrip();
  };

  const handleSavePDF = () => {
    handleClose();
    if (onSavePDF) onSavePDF();
  };

  const handleExportItinerary = () => {
    handleClose();
    if (onExportItinerary) onExportItinerary();
  };

  const handleExportTrip = () => {
    handleClose();
    if (onExportTrip) onExportTrip();
  };

  const handleImportTrip = () => {
    handleClose();
    if (onImportTrip) onImportTrip();
  };

  const handlePrint = () => {
    handleClose();
    if (onPrint) onPrint();
  };

  const handleSettings = () => {
    handleClose();
    if (onSettings) onSettings();
  };

  // Helper function to determine current page type for PDF export
  const getCurrentPageType = () => {
    const path = location.pathname;
    if (path.includes('/places')) return 'Places';
    if (path.includes('/travel')) return 'Travel';
    if (path.includes('/accommodation')) return 'Accommodation';
    if (path.includes('/documents')) return 'Documents';
    if (path.includes('/extras')) return 'Extras';
    if (path.includes('/day/')) return 'Day Detail';
    if (path.includes('/trips/')) return 'Days Overview';
    return 'Trip List';
  };

  // Don't render if user is not authenticated
  if (!currentUser) {
    return null;
  }

  return (
    <div className={`menu-item file-menu-container ${isOpen ? 'is-open' : ''}`} ref={menuRef}>
      <button
        id="file-menu-button"
        data-testid="file-menu"
        aria-controls={isOpen ? 'file-menu-dropdown' : undefined}
        aria-haspopup="true"
        aria-expanded={isOpen ? 'true' : undefined}
        onClick={handleClick}
        className="file-menu-trigger"
      >
        <FiFolder className="menu-icon file-icon" />
        <span>File</span>
      </button>

      {isOpen && (
        <div
          id="file-menu-dropdown"
          className="dropdown-menu file-menu-dropdown"
          role="menu"
          aria-labelledby="file-menu-button"
        >
          {/* New Trip - Always available */}
          <button
            onClick={handleNewTrip}
            data-testid="new-trip-menu-item"
            className="file-menu-item"
            role="menuitem"
          >
            New Trip
          </button>

          {/* Open Trip - Always available */}
          <button
            onClick={handleOpenTrip}
            data-testid="open-trip-menu-item"
            className="file-menu-item"
            role="menuitem"
          >
            Open Trip
          </button>

          <div className="menu-separator"></div>

          {/* Save PDF file - Available when viewing content pages */}
          <button
            onClick={handleSavePDF}
            data-testid="save-pdf-menu-item"
            className={`file-menu-item ${!isOnContentPage ? 'disabled' : ''}`}
            disabled={!isOnContentPage}
            title={!isOnContentPage ? 'Available when viewing trip content' : `Export ${getCurrentPageType()} as PDF`}
            role="menuitem"
          >
            Save PDF file
          </button>

          {/* Export Itinerary - Available when current trip exists */}
          <button
            onClick={handleExportItinerary}
            data-testid="export-itinerary-menu-item"
            className={`file-menu-item ${!hasActiveTripWithDays ? 'disabled' : ''}`}
            disabled={!hasActiveTripWithDays}
            title={!hasActiveTripWithDays ? 'No active trip selected' : 'Export itinerary as Excel/CSV'}
            role="menuitem"
          >
            Export Itinerary...
          </button>

          {/* Export Trip - Available when user has active trip and is owner or editor */}
          <button
            onClick={handleExportTrip}
            data-testid="export-trip-menu-item"
            className={`file-menu-item ${!hasActiveTripWithDays || isViewerOfSharedTrip ? 'disabled' : ''}`}
            disabled={!hasActiveTripWithDays || isViewerOfSharedTrip}
            title={
              !hasActiveTripWithDays
                ? 'No active trip selected'
                : isViewerOfSharedTrip
                  ? 'The selected Trip cannot be exported as you are not the owner or an editor'
                  : 'Export complete trip as ZIP'
            }
            role="menuitem"
          >
            Export Trip...
          </button>

          {/* Import Trip - Always available */}
          <button
            onClick={handleImportTrip}
            data-testid="import-trip-menu-item"
            className="file-menu-item"
            role="menuitem"
          >
            Import Trip...
          </button>

          <div className="menu-separator"></div>

          {/* Print - Show as disabled/coming soon */}
          <button
            onClick={handlePrint}
            data-testid="print-menu-item"
            className="file-menu-item disabled"
            disabled={true}
            title="Coming soon"
            role="menuitem"
          >
            Print
          </button>

          <div className="menu-separator"></div>

          {/* Settings - Link to existing settings */}
          <button
            onClick={handleSettings}
            data-testid="settings-menu-item"
            className="file-menu-item"
            role="menuitem"
          >
            Settings
          </button>
        </div>
      )}
    </div>
  );
};

export default FileMenu;